import { useEffect, useRef, useState } from 'react';

import { isMobile } from 'react-device-detect';

import { TOOLTIP_TIMEOUT } from '@/shared/constants/tooltip';

interface UseLongPressTooltipOptions {
    enabled?: boolean;
    delay?: number;
    autoCloseMs?: number;
}

export function useLongPressTooltip({
    enabled = true,
    delay = 500,
    autoCloseMs = TOOLTIP_TIMEOUT,
}: UseLongPressTooltipOptions = {}) {
    const [isOpen, setIsOpen] = useState(false);
    const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
    const autoCloseTimerRef = useRef<NodeJS.Timeout | null>(null);

    const clearLongPressTimer = () => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    };

    const clearAutoCloseTimer = () => {
        if (autoCloseTimerRef.current) {
            clearTimeout(autoCloseTimerRef.current);
            autoCloseTimerRef.current = null;
        }
    };

    useEffect(() => {
        if (!enabled) {
            clearLongPressTimer();
            clearAutoCloseTimer();
            queueMicrotask(() => setIsOpen(false));
        }
    }, [enabled]);

    useEffect(
        () => () => {
            clearLongPressTimer();
            clearAutoCloseTimer();
        },
        [],
    );

    const startLongPress = () => {
        if (!enabled || !isMobile) return;

        longPressTimerRef.current = setTimeout(() => {
            setIsOpen(true);
            clearAutoCloseTimer();
            autoCloseTimerRef.current = setTimeout(() => {
                setIsOpen(false);
            }, autoCloseMs);
        }, delay);
    };

    const endLongPress = () => {
        clearLongPressTimer();
    };

    return {
        open: isMobile ? isOpen : undefined,
        onOpenChange: isMobile ? setIsOpen : undefined,
        longPressHandlers: isMobile
            ? {
                  onTouchStart: startLongPress,
                  onTouchEnd: endLongPress,
                  onTouchCancel: endLongPress,
              }
            : {},
    };
}
